/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Log in to your account" — Figma file gtBsWqJJcI6ZBuBv51RU35 node
// 3281:98407. OTP-only flow: a single contact field forwards to
// /v1/auth/start. No password field — the backend exposes no password
// endpoint.

import { useState } from 'react';
import { View } from 'react-native';
import {
	AuthCrossLinkFooter,
	AuthField,
	AuthHeader,
	AuthInput,
	AuthLayout,
	AuthPrimaryButton,
} from '../AuthLayout';
import { detectContactKind } from '../contactDetect';

export interface WelcomeScreenProps {
	readonly onSubmit: (rawContact: string) => void;
	readonly onChooseNew: () => void;
	/**
	 * Optional "Link this device" affordance, shown below the Sign-in
	 * button when the host wires a pairing flow. When omitted the link
	 * is hidden — matching the legacy single-channel sign-in screen.
	 */
	readonly onLinkExistingDevice?: () => void;
	/**
	 * Optional "Restore from backup" affordance. Shown alongside
	 * "Link via QR" when the host wires a recovery flow. Omit to
	 * hide.
	 */
	readonly onRestoreFromBackup?: () => void;
	readonly externalError?: string | null;
	readonly isSubmitting?: boolean;
}

export function WelcomeScreen({
	onSubmit,
	onChooseNew,
	onLinkExistingDevice,
	onRestoreFromBackup,
	externalError,
	isSubmitting,
}: WelcomeScreenProps): JSX.Element {
	const [contact, setContact] = useState('');
	const [error, setError] = useState<string | null>(null);

	const fieldError = externalError ?? error;

	const handleSubmit = (): void => {
		const kind = detectContactKind(contact);
		if (kind === 'unknown') {
			setError('Enter a valid email or phone number');
			return;
		}
		setError(null);
		onSubmit(contact.trim());
	};

	const canSubmit = contact.trim().length > 0 && !isSubmitting;

	return (
		<AuthLayout accessibilityLabel="welcome">
			<AuthHeader title="Log in to your account" />

			<View style={{ flexDirection: 'column' }}>
				<AuthField label="Email or phone number" error={fieldError}>
					<AuthInput
						value={contact}
						onChangeText={(next) => {
							setContact(next);
							if (error !== null) setError(null);
						}}
						placeholder="Enter your email or phone number"
						autoCapitalize="none"
						hasError={Boolean(fieldError)}
						accessibilityLabel="ping_auth_welcome_contact"
					/>
				</AuthField>

				<View style={{ height: 32 }} />

				<AuthPrimaryButton
					onPress={handleSubmit}
					disabled={!canSubmit}
					accessibilityLabel="ping_auth_welcome_signin"
				>
					{isSubmitting ? 'Signing in…' : 'Sign in'}
				</AuthPrimaryButton>
			</View>

			<AuthCrossLinkFooter
				prompt="Don't have an account?"
				actionLabel="Sign up"
				onPressAction={onChooseNew}
			/>

			{onLinkExistingDevice != null && (
				<AuthCrossLinkFooter
					prompt="Already on another device?"
					actionLabel="Link via QR"
					onPressAction={onLinkExistingDevice}
				/>
			)}

			{onRestoreFromBackup != null && (
				<AuthCrossLinkFooter
					prompt="Lost every device?"
					actionLabel="Restore from backup"
					onPressAction={onRestoreFromBackup}
				/>
			)}

		</AuthLayout>
	);
}
